
import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatCurrency } from '@/lib/utils';

interface LineItem {
  id: string;
  description: string;
  manual_override: boolean;
  manual_total_cost: number | null;
  total_cost_snapshot: number;
}

interface LineItemSelectorProps {
  lineItems: LineItem[];
  selectedLineItems: string[];
  lineItemPercentages: { [key: string]: number };
  onSelectionChange: (itemId: string, checked: boolean) => void;
  onPercentageChange: (itemId: string, percentage: number) => void;
}

const LineItemSelector: React.FC<LineItemSelectorProps> = ({
  lineItems,
  selectedLineItems,
  lineItemPercentages,
  onSelectionChange,
  onPercentageChange
}) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Select Line Items to Invoice</h3>
      <div className="space-y-4 max-h-80 overflow-y-auto border rounded-md p-4">
        {lineItems.map((item) => {
          const isSelected = selectedLineItems.includes(item.id);
          const percentage = lineItemPercentages[item.id] || 100;
          const itemCost = item.manual_override && item.manual_total_cost !== null
            ? item.manual_total_cost
            : item.total_cost_snapshot;
          const invoiceAmount = (itemCost * percentage) / 100;

          return (
            <div key={item.id} className="space-y-2 p-3 border rounded-lg">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={item.id}
                  checked={isSelected}
                  onCheckedChange={(checked) => onSelectionChange(item.id, !!checked)}
                />
                <Label htmlFor={item.id} className="flex-1 text-sm font-medium">
                  {item.description}
                </Label>
                <span className="text-sm text-muted-foreground">
                  Total: {formatCurrency(itemCost)}
                </span>
              </div>
              
              {isSelected && (
                <div className="ml-6 flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Label htmlFor={`percentage-${item.id}`} className="text-sm">
                      Invoice %:
                    </Label>
                    <Input
                      id={`percentage-${item.id}`}
                      type="number"
                      max="100"
                      value={percentage}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') onPercentageChange(item.id, 0);
                        else { const num = parseInt(val); if (!isNaN(num)) onPercentageChange(item.id, num); }
                      }}
                      onBlur={(e) => {
                        const value = parseInt(e.target.value);
                        if (isNaN(value) || value < 1) {
                          onPercentageChange(item.id, 1);
                        } else if (value > 100) {
                          onPercentageChange(item.id, 100);
                        }
                      }}
                      className="w-20"
                    />
                    <span className="text-sm">%</span>
                  </div>
                  <div className="text-sm font-medium text-green-600">
                    Invoice Amount: {formatCurrency(invoiceAmount)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LineItemSelector;
